import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useUser } from '../../../shared/context/UserContext';
import { getToolsBySite, transformToolForInventory, getSites, createInventoryTool, searchInventoryTools } from '../api/inventoryApi';
import CustomizeViewDrawer, { useCustomizeView } from '../components/CustomizeViewDrawer';
import SyncChip from '../../../shared/components/SyncChip';
import InventoryBottomBar from '../../../shared/components/InventoryBottomBar';
import OutdoorToggle, { useOutdoorMode } from '../../../shared/components/OutdoorToggle';
import { computeMatchContext, renderHighlightedValue } from '../../../shared/utils/matchContext';
import './ToolListScreen.css';

function statusIcon(status) {
  if (status === 'FOUND') return { char: '✓', cls: 'found' };
  if (status === 'ADDED') return { char: '+', cls: 'added' };
  return { char: '○', cls: 'not-yet' };
}

/* Field key → tool property mapping for pinned columns */
const FIELD_TO_PROP = {
  alt_tool_id: 'altId', tool_type: 'toolType', quantity: 'quantity',
  cavities: 'cavities', die_op: 'opNumber', tool_code: 'toolCode',
  site_location: 'siteLocation', detail_location: 'detailLocation',
  row_nbr: 'rowNbr', stack_nbr: 'stackNbr', rack_nbr: 'rackNbr',
  shelf_nbr: 'shelfNbr', skid_nbr: 'skidNbr',
  length: 'length', width: 'width', height: 'height',
  estimated_weight: 'estimatedWeight', weight_unit: 'weightUnit',
};

/* Derive branch base path, e.g. "/disposal/inventory" from "/disposal/inventory/SITE123" */
function branchInventoryBase(pathname) {
  const match = pathname.match(/^(\/[^/]+)\/inventory/);
  return match ? `${match[1]}/inventory` : '/disposal/inventory';
}

export default function ToolListScreen() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { callerEmail } = useUser();
  const invBase = branchInventoryBase(location.pathname);

  const mode = location.pathname.startsWith('/lifecycle') ? 'lifecycle' : 'disposal';
  const [siteName, setSiteName] = useState(location.state?.siteName || '');

  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Search query lives in the URL (?q=...) so it survives back-navigation from tool detail.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('q') || '';
  const setSearch = (v) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (v) next.set('q', v);
      else next.delete('q');
      return next;
    }, { replace: true });
  };
  const [showCustomize, setShowCustomize] = useState(false);
  const customizeView = useCustomizeView(siteId);
  const { outdoor, toggleOutdoor } = useOutdoorMode();

  const searchRef = useRef(null);

  /* ── Create-tool modal state ─────────────────────────────────────── */
  const [showCreate, setShowCreate] = useState(false);
  const [createId, setCreateId] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createWarning, setCreateWarning] = useState(null);
  const [siteOem, setSiteOem] = useState(null);

  /* ── Resolve site name if not passed via route state ───────────────── */
  useEffect(() => {
    getSites(callerEmail).then(sites => {
      const site = sites.find(s => s.site_id === siteId);
      if (site) {
        if (!location.state?.siteName) {
          const name = site.site_city
            ? `${site.site_name} (${site.site_city})`
            : site.supplier_site_code
              ? `${site.site_name} (${site.supplier_site_code})`
              : site.site_name || 'Site';
          setSiteName(name);
        }
        setSiteOem(site.oem_primary || null);
      }
    }).catch(() => {}); // best-effort
  }, [siteId, callerEmail, location.state?.siteName]);

  /* ── Load tools for this site ───────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getToolsBySite(siteId, callerEmail)
      .then(rawTools => {
        if (!cancelled) {
          setTools((rawTools || []).map(transformToolForInventory));
        }
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Failed to load tools');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [siteId, callerEmail]);

  /* ── Server search state ──────────────────────────────────────────── */
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const debounceRef = useRef(null);

  const runServerSearch = useCallback((q) => {
    if (!siteId || !callerEmail) return;
    setSearchLoading(true);
    setSearchError(null);
    searchInventoryTools(siteId, q, callerEmail)
      .then(raw => setSearchResults((raw || []).map(r => {
        const tool = transformToolForInventory(r);
        // Local compute first (covers Tool ID / Alt ID / Location, all 6 fields).
        // Fall back to the backend _match_context (only emitted for asset_tag /
        // part_number hits) when local returns null — the transform drops PNs/ATs
        // so local can't detect those matches.
        let matchContext = computeMatchContext(tool, q);
        if (!matchContext && r._match_context) {
          matchContext = {
            label: r._match_context.field === 'asset_tag' ? 'Asset Tag' : 'Part #',
            value: r._match_context.value,
          };
        }
        return { tool, matchContext };
      })))
      .catch(err => setSearchError(err.message))
      .finally(() => setSearchLoading(false));
  }, [siteId, callerEmail]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setSearchResults(null);
      setSearchError(null);
      setSearchLoading(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runServerSearch(q), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, runServerSearch]);

  /* ── Client-side filtering (fallback for short queries) ──────────── */
  const clientFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tools.map(t => ({ tool: t, matchContext: null }));
    return tools
      .filter(t =>
        (t.toolId || '').toLowerCase().includes(q) ||
        (t.altId && t.altId.toLowerCase().includes(q)) ||
        (t.toolType || '').toLowerCase().includes(q) ||
        (t.siteLocation && t.siteLocation.toLowerCase().includes(q)) ||
        (t.detailLocation && t.detailLocation.toLowerCase().includes(q)) ||
        // The five can load as numbers, so String() before .toLowerCase().
        [t.rowNbr, t.stackNbr, t.rackNbr, t.shelfNbr, t.skidNbr]
          .some(v => v != null && String(v).toLowerCase().includes(q))
      )
      .map(t => ({ tool: t, matchContext: computeMatchContext(t, search) }));
  }, [search, tools]);

  const displayList = searchResults !== null ? searchResults : clientFiltered;

  const foundCount = tools.filter(t => t.status === 'FOUND').length;
  const totalCount = tools.length;
  const pct = totalCount > 0 ? Math.round((foundCount / totalCount) * 100) : 0;

  /* ── Create tool handler ─────────────────────────────────────────── */
  function goToCreatedTool(doc) {
    navigate(`${invBase}/${siteId}/${doc.tool_id || doc.id}`, {
      replace: true,
      state: { mode, hiddenSections: [...customizeView.hiddenPanels], siteName }
    });
  }

  function openCreatedTool() {
    setShowCreate(false);
    goToCreatedTool(createWarning.doc);
  }

  async function handleCreateTool(supplierToolId) {
    setCreating(true);
    setCreateError(null);
    setCreateWarning(null);
    try {
      const doc = await createInventoryTool(siteId, supplierToolId, siteOem, callerEmail);
      if (doc.duplicate_warning) {
        setCreateWarning({ warning: doc.duplicate_warning, doc });
      } else {
        setShowCreate(false);
        goToCreatedTool(doc);
      }
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className={`tl-screen${outdoor ? ' outdoor-mode' : ''}`}>
      {/* ── Sticky Header ────────────────────────────────────────────── */}
      <header className="tl-header">
        <div className="tl-header-top">
          <button className="tl-back" onClick={() => navigate(invBase)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="tl-header-text">
            <h1 className="tl-site-name">{siteName || '...'}</h1>
            <p className="tl-site-sub">
              {loading ? 'Loading...' : `${foundCount} / ${totalCount} tools reviewed`}
            </p>
          </div>
          <div className="tl-header-actions">
            <OutdoorToggle outdoor={outdoor} onToggle={toggleOutdoor} />
            <SyncChip />
            <button
              className="tl-btn-configure"
              onClick={() => setShowCustomize(true)}
              aria-label="Configure sections"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="7" cy="5" r="2" fill="currentColor"/>
                <circle cx="13" cy="10" r="2" fill="currentColor"/>
                <circle cx="9" cy="15" r="2" fill="currentColor"/>
              </svg>
            </button>
            <button
              className="tl-btn-add"
              onClick={() => { setCreateId(''); setCreateError(null); setShowCreate(true); }}
              aria-label="Add new tool"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ── Progress bar ───────────────────────────────────────────── */}
        <div className="tl-progress-track">
          <div className="tl-progress-fill" style={{ width: `${pct}%` }} />
        </div>

        {/* ── Search ─────────────────────────────────────────────────── */}
        <div className="tl-search-wrap">
          <svg className="tl-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            ref={searchRef}
            type="text"
            className="tl-search-input"
            placeholder="Search ID, tag, part number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {searchLoading && <div className="tl-search-loading" />}
        </div>
        {searchError && <div className="tl-search-error">{searchError}</div>}
      </header>

      {/* ── Tool rows ────────────────────────────────────────────────── */}
      <div className="tl-list">
        {/* Loading state */}
        {loading && (
          <div className="tl-empty">Loading tools...</div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="tl-error">
            <p className="tl-error-message">Failed to load tools</p>
            <p className="tl-error-detail">{error}</p>
            <button
              className="tl-error-retry"
              onClick={() => {
                setError(null);
                setLoading(true);
                getToolsBySite(siteId, callerEmail)
                  .then(raw => setTools((raw || []).map(transformToolForInventory)))
                  .catch(err => setError(err.message))
                  .finally(() => setLoading(false));
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty search results */}
        {!loading && !error && displayList.length === 0 && (
          <div className="tl-empty">
            {search.trim() ? 'No tools match your search.' : 'No tools at this site.'}
          </div>
        )}

        {/* Tool list */}
        {!loading && !error && displayList.map(({ tool, matchContext }) => {
          const icon = statusIcon(tool.status);
          return (
            <button
              key={tool.id}
              className="tl-row"
              onClick={() => navigate(`${invBase}/${siteId}/${tool.id}`, {
                state: { mode, hiddenSections: [...customizeView.hiddenPanels], siteName }
              })}
            >
              <span className={`tl-status-circle ${icon.cls}`}>{icon.char}</span>
              <div className="tl-row-info">
                <div className="tl-row-top">
                  <span className="tl-tool-id">{tool.toolId}</span>
                  {tool.altId && <span className="tl-alt-id">{tool.altId}</span>}
                </div>
                <div className="tl-row-sub">
                  {tool.toolType}{tool.opNumber ? ` · ${tool.opNumber}` : ''}
                </div>
                {matchContext && (
                  <div className="tl-match-badge">
                    Matched: {matchContext.label} · {renderHighlightedValue(matchContext.value, search)}
                  </div>
                )}
                {customizeView.pinnedFields.length > 0 && (
                  <div className="tl-pinned-line">
                    {customizeView.pinnedFields.slice(0, 2).map(pf => {
                      const val = tool[FIELD_TO_PROP[pf.fieldKey]];
                      if (!val && val !== 0) return null;
                      return <span key={pf.fieldKey} className="tl-pinned-item">{pf.label}: {val}</span>;
                    })}
                  </div>
                )}
              </div>
              <div className="tl-row-right">
                {tool.photoCount > 0 && (
                  <span className="tl-photo-badge">📷 {tool.photoCount}</span>
                )}
                <span className={`tl-status-label ${icon.cls}`}>{tool.status}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Customize View Drawer ──────────────────────────────────── */}
      <CustomizeViewDrawer
        open={showCustomize}
        onClose={() => setShowCustomize(false)}
        mode={mode}
        customizeView={customizeView}
      />

      {/* ── Create Tool Modal ─────────────────────────────────────────── */}
      {showCreate && (
        <div
          className="tl-modal-backdrop"
          onClick={() => {
            if (createWarning) openCreatedTool();
            else if (!creating) setShowCreate(false);
          }}
        >
          <div className="tl-create-modal" onClick={e => e.stopPropagation()}>
            {createWarning ? (
              <>
                <h2 className="tl-create-title">Tool created.</h2>
                <p className="tl-create-warning">{createWarning.warning.message}</p>
                {createWarning.warning.existing_tool_ids?.length > 0 && (
                  <ul className="tl-create-warning-ids">
                    {createWarning.warning.existing_tool_ids.map(id => (
                      <li key={id}>{id}</li>
                    ))}
                  </ul>
                )}
                <div className="tl-create-actions">
                  <button
                    type="button"
                    className="tl-create-btn-go"
                    onClick={openCreatedTool}
                  >
                    Open the new tool
                  </button>
                </div>
              </>
            ) : (
            <>
            <h2 className="tl-create-title">Supplier Tool ID</h2>
            <input
              type="text"
              className="tl-create-input"
              placeholder="e.g. AK-STM-0099 (optional)"
              value={createId}
              onChange={e => setCreateId(e.target.value)}
              disabled={creating}
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleCreateTool(createId.trim()); }}
            />
            {createError && <p className="tl-create-error">{createError}</p>}
            <div className="tl-create-actions">
              <button
                className="tl-create-btn-skip"
                onClick={() => handleCreateTool(null)}
                disabled={creating}
              >
                Skip
              </button>
              <button
                className="tl-create-btn-go"
                onClick={() => handleCreateTool(createId.trim())}
                disabled={creating}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {/* ── Bottom Bar ─────────────────────────────────────────────────── */}
      <InventoryBottomBar
        onAdd={() => { setCreateId(''); setCreateError(null); setShowCreate(true); }}
        onSearch={() => searchRef.current?.focus()}
      />
    </div>
  );
}
